import { RouterProvider } from 'react-router-dom';
import './App.scss';
import { Approuter } from './router';
import * as Sentry from '@sentry/react';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect } from 'react';
import Button from './component/ui/Button';
import Modal from './component/modal/Modal';
import { clearNotification } from './store/action/notifications';
import { setAuthToken } from './store/action/userInfo';
import { setCloseSessionModal } from './store/action/session';
import { sessionContinue, logoutUser } from './services/auth';
import { setAuthTokenToLocalStorage } from './utils';
import { setAxiosDispatch } from './services/axios';

const App = () => {
	const dispatch = useDispatch();
	const user = useSelector((state) => state.userInfo);

	const initSentry = useCallback(() => {
		const { userInfo } = user;
		if (userInfo && userInfo?.data?.user_id) {
			Sentry.setUser({
				id: userInfo.data.user_id,
				organisation_id: userInfo.data.organisation_id,
				name: `${userInfo.data.first_name} ${userInfo.data.last_name}`,
				email: userInfo?.data?.email,
			});
		} else {
			Sentry.setUser(null);
		}
	}, [user]);

	useEffect(() => {
		if (user && user?.userInfo && user.isAuthenticated) {
			initSentry();
		}
	}, [user, initSentry]);

	const showSessionModal = useSelector((state) => state.sessionModal.showSessionModal);
	const getUserSubdomainFromUrl = () => {
		const match = window.location.pathname.match(/^\/user\/([^/]+)/);
		return match ? match[1] : null;
	};
	const handleLogout = async () => {
		try {
			await logoutUser();
		} catch (err) {
			console.error('Logout error:', err);
		}
		if (dispatch) {
			dispatch(clearNotification());
			dispatch(setAuthToken({ authToken: null }));
			dispatch(setCloseSessionModal());
		}
		localStorage.clear();
		const subdomain = getUserSubdomainFromUrl();
		if (subdomain) {
			window.location.replace(`/user/${subdomain}`);
		} else {
			window.location.replace('/');
		}
	};

	const handleContinueSession = async () => {
		try {
			const data = await sessionContinue();

			// The endpoint returns { token, refreshToken }. This previously stored
			// data.refreshToken as the auth token — so every subsequent request
			// sent the 7-day REFRESH token where the access token belongs. It
			// happened to be accepted because the auth middleware doesn't check
			// token_type, which is exactly why it went unnoticed.
			if (!data?.success || !data?.token) {
				handleLogout();
				return;
			}

			setAuthTokenToLocalStorage(data.token);
			if (data.refreshToken) {
				localStorage.setItem('refreshToken', data.refreshToken);
			}

			dispatch(setCloseSessionModal());
			setTimeout(() => {
				window.location.reload();
			}, 100);
		} catch (error) {
			handleLogout();
		}
	};

	useEffect(() => {
		setAxiosDispatch(dispatch);
	}, [dispatch]);

	return (
		<div className="wrapper">
			<RouterProvider router={Approuter} />
			{showSessionModal && (
				<Modal
					className="delivery-modal"
					title="Session Expired"
					isOpen={showSessionModal}
					onClose={() => handleLogout()}
					cancelButton={false}
				>
					<div
						style={{
							textAlign: 'center',
							backgroundColor: '#E6F5FF',
							padding: '20px 10px',
							marginBottom: '20px',
							borderRadius: '5px',
							fontWeight: '600',
							color: 'black',
						}}
					>
						Your session is about to expire or has expired. Do you want to continue?
					</div>

					<div className="d-flex justify-content-end gap-4">
						<Button
							button="Continue Session"
							type="button"
							className="btn btn-primary"
							onClick={() => handleContinueSession()}
						/>
						<Button
							button="Logout"
							type="button"
							className="btn btn-danger"
							onClick={() => handleLogout()}
						/>
					</div>
				</Modal>
			)}
		</div>
	);
};

export default App;
